/* eslint-disable max-len */
import * as React from 'react';
import styles from './info-text.scss';
import { DisplayTextWithLinks } from 'ui/common/display-text-with-links';

export enum InfoType {
  Text,
  Button
}
/**
 * Describes how the Info text component is displayed.
 * > Without define visible prop component does not appear.
 */
export interface InfoTextProps {
  /**
   * Define whether the content of component should be visible. Default is 'undefined'.
   */
  visible?: boolean;
  /**
   * Define content of component. Default is 'undefined'.
   */
  text?: string;
  /**
   * Define type of component. Default is 'undefined'.
   */
  type?: InfoType;
  /**
   * Define whether the urls in the text should be clickable. Default is 'true'.
   */
  linkify?: boolean;
  /**
   * Override styles.
   */
  className?: string;
  /**
   * Override info icon.
   */
  infoIcon?: any;
  /**
   * Define whether the component is using react router link.
   */
  linkRouter?: boolean;
}

export function InfoText({
  visible,
  text,
  linkify = true,
  className,
  infoIcon,
  linkRouter
}: InfoTextProps) {
  const infoTextTriggerRef: any = React.createRef();
  const infoTextRef: any = React.createRef();

  const [showInfoTextDownwards, setShowInfoTextDownwards] = React.useState(false);
  const [touchStart, setTouchStart] = React.useState(false);
  const MAX_LENGTH_INFOTEXT = 1024;
  const MAX_CHARACTERS_SINGLE_LINE = 45;

  const truncatedText = text?.substring(0, MAX_LENGTH_INFOTEXT);
  const exceedsMaxLength = text?.length > MAX_LENGTH_INFOTEXT;
  const textOverride = exceedsMaxLength ? truncatedText + '...' : text;
  const infoText = <DisplayTextWithLinks text={textOverride} linkify={linkify} linkRouter={linkRouter} />;

  const shouldOpenInfoTextDownwards = (): boolean => {
    if (!infoTextRef.current) {
      return false;
    }
    const infoTextTriggerRefRect = infoTextTriggerRef?.current?.getBoundingClientRect();

    const newlinesCounter = text?.match(/\n\n*/g)?.length;
    const emptySpacesDueToNewLinesInApprox = !!newlinesCounter && (newlinesCounter * MAX_CHARACTERS_SINGLE_LINE);
    const textLengthIncludingNewLines = truncatedText?.length + emptySpacesDueToNewLinesInApprox;
    //detect unicode characters e.g. Arabic, Chinese, Japanese, Korean, Thai, Vietnamese, etc.
    // eslint-disable-next-line no-control-regex
    const hasUnicodeCharacters = !!text?.match(/[^\u0000-\u05C0\u2100-\u214F]+/g)?.length;

    const topPos1 = hasUnicodeCharacters ? 900 : 550;
    const topPos2 = hasUnicodeCharacters ? 700 : 480;
    const topPos3 = hasUnicodeCharacters ? 500 : 350;
    const topPos4 = hasUnicodeCharacters ? 400 : 280;
    const topPos5 = hasUnicodeCharacters ? 200 : 200;

    if (textLengthIncludingNewLines >= MAX_LENGTH_INFOTEXT) {
      return true;
    }
    else if (textLengthIncludingNewLines > 800 && infoTextTriggerRefRect?.y < topPos1) {
      return true;
    }
    else if (textLengthIncludingNewLines > 600 && infoTextTriggerRefRect?.y < topPos2) {
      return true;
    }
    else if (textLengthIncludingNewLines > 400 && infoTextTriggerRefRect?.y < topPos3) {
      return true;
    }
    else if (textLengthIncludingNewLines > 200 && infoTextTriggerRefRect?.y < topPos4) {
      return true;
    }
    else if (textLengthIncludingNewLines > 70 && infoTextTriggerRefRect?.y < topPos5) {
      return true;
    }
    else {
      return false;
    }
  };

  const helpIconDefault = <svg viewBox="0 0 20 20" height="20px" width="12px" className={`${styles.helpIconInfotext}`} data-testid='InfoText'>
    <path className={styles.tooltipArrow} d="M4,0H16L10,6Z" transform="translate(-4)" />
    <path fill="#0f204b" d="M10,7.1623a5.806,5.806,0,1,0,5.806,5.806h0A5.808,5.808,0,0,0,10,7.1623Z" transform="translate(-4)" />
    <rect fill="white" x="5.4185" y="15.9023" width="1.162" height="1.161" />
    <path fill="white" d="M11.78,12.5633l-.521.53a1.977,1.977,0,0,0-.679,1.643H9.4185v-.29a2.337,2.337,0,0,1,.675-1.639l.72-.732a1.135,1.135,0,0,0,.343-.819,1.161,1.161,0,1,0-2.322,0H7.6775a2.3225,2.3225,0,0,1,4.645,0,1.848,1.848,0,0,1-.541,1.308Z" transform="translate(-4)" />
  </svg>;

  const helpIconInfotextDownwards = <svg viewBox="0 0 20 20" height="20px" width="12px" className={`${styles.helpIconInfotext}`}  data-testid='InfoTextDownwards'>
    <path className={`${styles.tooltipArrow} ${styles.tooltipArrowUp}`} d="M6 15l5-6 6 6z" />
    <path fill="#0f204b" d="M10,7.1623a5.806,5.806,0,1,0,5.806,5.806h0A5.808,5.808,0,0,0,10,7.1623Z" transform="translate(-4)" />
    <rect fill="white" x="5.4185" y="15.9023" width="1.162" height="1.161" />
    <path fill="white" d="M11.78,12.5633l-.521.53a1.977,1.977,0,0,0-.679,1.643H9.4185v-.29a2.337,2.337,0,0,1,.675-1.639l.72-.732a1.135,1.135,0,0,0,.343-.819,1.161,1.161,0,1,0-2.322,0H7.6775a2.3225,2.3225,0,0,1,4.645,0,1.848,1.848,0,0,1-.541,1.308Z" transform="translate(-4)" />
  </svg>;

  const helpIcon = showInfoTextDownwards ? helpIconInfotextDownwards : helpIconDefault;
  const infoTextStyleDownwards = showInfoTextDownwards ? styles.infoTextDownwards : '';

  const handleOnMouseEnter = () => {
    !touchStart && setShowInfoTextDownwards(shouldOpenInfoTextDownwards);
    setTouchStart(false);
  };

  const handleOnTouchStart = () => {
    setShowInfoTextDownwards(shouldOpenInfoTextDownwards);
    setTouchStart(true);
  };

  return visible ?
    <span className={`${styles.infoTextTrigger} ${className}`} ref={infoTextTriggerRef}
      onMouseEnter={handleOnMouseEnter} onTouchStart={handleOnTouchStart}>
      {infoIcon || helpIcon}
      <div className={`${styles.infoText} ${infoTextStyleDownwards} infotext`} ref={infoTextRef}>{infoText}</div>
    </span> : null;

}

InfoText.displayName = 'InfoText';
